import { defineStore } from 'pinia';
import { DatasourceSingleton } from '@/common/di';
import {
  CentreEntity,
  DropdownSelected,
  EntitySubType,
  HazardEntity,
  HazardsEntity,
  HazardsRepositoryImpl,
  HazardType,
  hazardTypeRoute,
  HazardView,
  IncidentEntity,
  MapMode,
  naturalHazardTypes,
  SelectedHazard,
  SelectedHazardViewType,
  typeSections,
  useMapStore,
  WarningEntity,
  MapGuideFireDanger,
  UiListTileLeadingColor,
  UiListTileBackgroundColor,
  HAZARD_MARKERS,
  WarningSubTypes,
  ClosureEntity,
  TotalFireBanEntity,
  replaceEarthquakeCircleFeatures,
} from '@/features/hazards';
import { fold } from 'fp-ts/lib/Either';
import { pipe } from 'fp-ts/lib/function';
import { Feature, FeatureCollection, GeoJsonProperties, Geometry } from 'geojson';
import { DateTime } from 'luxon';
import { container } from 'tsyringe';
import { computed, ref } from 'vue';
import { showBothHazardView } from '@/common/helpers/sizes';
import { useRouter } from 'vue-router';
import { RouteNames, pageTitleHandler } from '@/router';
import { useOnline } from '@vueuse/core';
import { buildFdrDates } from '@/common';

export const useHazardsStore = defineStore('hazardsStore', () => {
  // This is to check if the user is online and gracefully handle the offline state
  // @see - https://vueuse.org/core/useOnline/
  const isOnline = useOnline();

  // TODO: datasource and repository should be injected as arguments
  const datasource = container.resolve(DatasourceSingleton).hazards;
  const s3Datasource = container.resolve(DatasourceSingleton).s3Hazards;

  const router = useRouter();

  const mapStore = useMapStore();

  const hazardRepository = new HazardsRepositoryImpl(datasource, s3Datasource);

  const searchTerm = ref<string>('');
  const mapMode = ref<MapMode>(MapMode.Default);
  const selectedDateIndex = ref<number>(0);

  const dates = computed(() => {
    if (![MapMode.FireBan, MapMode.FireDanger].includes(mapMode.value)) {
      return [];
    } else if (mapMode.value === MapMode.FireBan) {
      return ['Today', 'Tomorrow'];
    }

    return buildFdrDates();
  });

  const selectedHazard = ref<SelectedHazard | null>(null);

  const typesFilters = ref<DropdownSelected>(
    Object.fromEntries(typeSections.map((section) => [section.label, Object.fromEntries(section.items.map((item) => [item.label, false]))]))
  );

  const isSomeTypeFilterActive = computed<boolean>(() =>
    Object.values(typesFilters.value).some((section) => Object.values(section).some((checked) => checked))
  );

  const hazardView = ref<HazardView>(!showBothHazardView.value ? HazardView.List : HazardView.Both);

  const activeView = computed<HazardView>(() =>
    !showBothHazardView.value && hazardView.value === HazardView.Both ? HazardView.Map : hazardView.value
  );

  const setActiveView = (view: HazardView) => {
    if (view === hazardView.value) return;

    let sanitisedView = view;

    if (!showBothHazardView.value && sanitisedView === HazardView.Both) {
      sanitisedView = HazardView.Map;
    }

    router.push({ query: { ...(router.currentRoute.value.query ?? {}), view: sanitisedView.toLowerCase() } });

    hazardView.value = sanitisedView;
  };

  const setMapMode = (mode: MapMode) => {
    if (mode === mapMode.value) return;

    const query = { view: hazardView.value.toLowerCase() };

    if ([MapMode.FireBan, MapMode.FireDanger].includes(mode)) {
      router.push({
        path: mode === MapMode.FireBan ? hazardTypeRoute(HazardType.TotalFireBans) : hazardTypeRoute(HazardType.FireDangerRating),
        query,
      });
    } else {
      router.push({ name: RouteNames.Hazards, query });
    }

    mapMode.value = mode;

    // Reset the selected date index to prevent the date from being out of bounds
    selectedDateIndex.value = 0;
  };

  const setSelectedDateIndex = (index: number) => {
    if (index === selectedDateIndex.value || index < 0 || index > 3) return;

    let sanitisedIndex = index;

    if (mapMode.value === MapMode.FireBan) {
      sanitisedIndex = Math.min(1, index);
    } else if (mapMode.value === MapMode.FireDanger) {
      sanitisedIndex = Math.min(3, index);
    }

    router.push({ query: { ...(router.currentRoute.value.query ?? {}), date: sanitisedIndex.toString() } });

    selectedDateIndex.value = sanitisedIndex;
  };

  const setSelectedHazard = (hazard: SelectedHazard | null) => {
    let eventId = hazard?.eventId;

    if (hazard?.hazardType) {
      const hazardEntity = (hazards.value[hazard?.hazardType] as HazardEntity[])?.find(
        (h: HazardEntity) => h.id === hazard?.hazardId
      ) as HazardEntity;

      if (hazardEntity) {
        const { title } = hazardEntity;

        if (!eventId?.length) {
          eventId = hazardEntity?.eventId;
        }

        if (title?.length) {
          const pageTitle = `${title} - ${pageTitleHandler.value}`;

          pageTitleHandler.value = pageTitle;
        }
      }
    }

    selectedHazard.value = hazard ? { ...hazard, eventId: eventId ?? '' } : null;
  };

  const retryButton = ref(false);

  const isLoadingHazards = ref(false);
  const isLoadingEnrichedWarning = ref(false);

  const hazards = ref<HazardsEntity>({
    incidents: [],
    warnings: [],
    centres: [],
    closures: [],
    events: [],
    totalFireBans: [],
    fireDangerRatings: [],
    prescribedBurns: [],
  });

  const filteredSectionHazards = computed(() => {
    const activeFilters: Set<(typeof EntitySubType)[keyof typeof EntitySubType]> = new Set();

    //! Warnings filters
    const warningSection = typeSections.find((typeSection) => typeSection.label === 'Warnings');
    Object.entries(typesFilters.value.Warnings)
      .filter((warningFilter) => warningFilter[1])
      .forEach(([filter]) =>
        warningSection?.items.find((item) => item.label === filter)?.hazardSubType?.forEach((warningType) => activeFilters.add(warningType))
      );

    // Filter out total fire bans (until the API is update to not include them in warnings endpoint)
    const warnings = hazards.value.warnings.filter(
      (warning) =>
        warning.entitySubType !== WarningSubTypes.TotalFireBanWarningEntity &&
        warning.entitySubType !== WarningSubTypes.PowerOutageWarningEntity
    );
    const filteredWarnings = !isSomeTypeFilterActive.value
      ? warnings
      : warnings.filter((warning) => activeFilters.has(warning.entitySubType!));

    //! Natural hazards
    const naturalHazardsChecked = typesFilters.value['Natural Hazards']?.['Default'];
    const naturalHazards = hazards.value.incidents.filter((incident) => naturalHazardTypes.includes(incident.incidentType));
    const filteredNaturalHazards = !isSomeTypeFilterActive.value || naturalHazardsChecked ? naturalHazards : [];

    //! Incidents filters
    const incidentChecked = typesFilters.value.Incidents?.['Default'];

    const nonSpecialIncidents = hazards.value.incidents.filter(
      (incident) => incident.title !== 'Burn Off' && !naturalHazardTypes.includes(incident.incidentType)
    );
    const filteredIncidents = !isSomeTypeFilterActive.value || incidentChecked ? nonSpecialIncidents : [];

    //! Outages and closures
    const outagesAndClosuresSection = typeSections.find((typeSection) => typeSection.label === 'Outages and Closures');
    Object.entries(typesFilters.value['Outages and Closures'])
      .filter((outagesOrClosureFilter) => outagesOrClosureFilter[1])
      .forEach(([filter]) =>
        outagesAndClosuresSection?.items
          .find((item) => item.label === filter)
          ?.hazardSubType?.forEach((outagesOrClosureType) => activeFilters.add(outagesOrClosureType))
      );

    const filteredOutagesAndClosures = !isSomeTypeFilterActive.value
      ? hazards.value.closures
      : hazards.value.closures.filter((outageOrClosure) => activeFilters.has(outageOrClosure.entitySubType!));

    //! Centres
    const centresSection = typeSections.find((typeSection) => typeSection.label === 'Centres');
    Object.entries(typesFilters.value.Centres)
      .filter((centreFilter) => centreFilter[1])
      .forEach(([filter]) =>
        centresSection?.items.find((item) => item.label === filter)?.hazardSubType?.forEach((centreType) => activeFilters.add(centreType))
      );

    const centres = hazards.value.centres;
    const filteredCentres = !isSomeTypeFilterActive.value ? centres : centres.filter((centre) => activeFilters.has(centre.entitySubType!));

    //! Prescribed burns and burn offs
    const prescribedBurnsAndBurnOffsChecked = typesFilters.value['Prescribed Burns/Burn Offs']?.['Default'];

    const prescribedBurnsAndBurnOffs = [
      ...hazards.value.prescribedBurns,
      ...hazards.value.incidents.filter((incident) => incident.title === 'Burn Off'),
    ];

    const filteredPrescribedBurnsAndBurnOffs =
      !isSomeTypeFilterActive.value || prescribedBurnsAndBurnOffsChecked ? prescribedBurnsAndBurnOffs : [];

    //! Fire Danger Ratings
    const filtered = {
      incidents: filteredIncidents,
      warnings: filteredWarnings,
      centres: filteredCentres,
      outagesAndClosures: filteredOutagesAndClosures,
      prescribedBurnsAndBurnOffs: filteredPrescribedBurnsAndBurnOffs,
      naturalHazards: filteredNaturalHazards,
      totalFireBans: getTotalFireBansByDateIndex(selectedDateIndex.value),
      fireDangerRatings: hazards.value.fireDangerRatings,
    };

    return filtered;
  });

  const activeTotalFireBansForToday = computed(() => {
    const activeFireBanForToday = getTotalFireBansByDateIndex(0)
      .filter((fireBan) => fireBan.banLevel === 'Ban')
      .at(0) as HazardEntity | undefined;

    if (!activeFireBanForToday) return;

    return {
      ...activeFireBanForToday,
      title: 'Total Fire Bans',
      description: 'are declared for today',
    };
  });

  const extremeOrCatastrophicFireDangerRatingForToday = computed(() => {
    const fireDangerRatingForToday = filteredSectionHazards?.value.fireDangerRatings.find((hazard) => {
      return [MapGuideFireDanger.Catastrophic, MapGuideFireDanger.Extreme].includes(
        `${hazard.forecasts?.[0]?.fireDanger}` as MapGuideFireDanger
      );
    });
    if (!fireDangerRatingForToday) return;

    return {
      ...fireDangerRatingForToday,
      entityType: HazardType.FireDangerRating,
      title: 'Extreme or Catastrophic Fire Danger Ratings',
      description: 'are declared for today',
      backgroundColor: UiListTileBackgroundColor.Orange,
      leadingColor: UiListTileLeadingColor.Orange,
      icon: HAZARD_MARKERS['ew-extreme-or-catastrophic-fdr'],
    } as HazardEntity;
  });

  const getHazardsByEvent = (eventId?: string) => {
    return {
      incidents: filteredSectionHazards.value.incidents.filter((incident) => incident.eventId === eventId) as IncidentEntity[],
      warnings: filteredSectionHazards.value.warnings.filter((warning) => warning.eventId === eventId) as WarningEntity[],
      outagesAndClosures: filteredSectionHazards.value.outagesAndClosures.filter(
        (outagesAndClosure) => outagesAndClosure.eventId === eventId
      ) as ClosureEntity[],
      centres: filteredSectionHazards.value.centres.filter((centre) => centre.eventId === eventId) as CentreEntity[],
      prescribedBurnsAndBurnOffs: filteredSectionHazards.value.prescribedBurnsAndBurnOffs.filter(
        (burnOrBurnOff) => burnOrBurnOff.eventId === eventId
      ) as IncidentEntity[],
      naturalHazards: filteredSectionHazards.value.naturalHazards.filter(
        (naturalHazard) => naturalHazard.eventId === eventId
      ) as IncidentEntity[],
    };
  };

  const fetchAllHazards = async (hazardTypes: HazardType[]) => {
    if (isOnline.value) {
      isLoadingHazards.value = true;
      const fetchPromises = hazardTypes.map((hazardType) => fetchHazards(hazardType));
      await Promise.all(fetchPromises);
      isLoadingHazards.value = false;
    }
  };

  const fetchHazards = async (hazardType: HazardType) => {
    const maybeHazards = await hazardRepository.getHazards(hazardType);
    pipe(
      maybeHazards,
      fold(
        () => {
          // toast.addToast({
          //   text: mapErrorToMessage(error),
          //   scheme: 'error',
          // });
        },
        (hazard) => {
          // Guaranteed to be a hazard of the correct type
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          hazards.value[hazardType] = hazard as any[];
        }
      )
    );
  };

  /**
   * This will filter the fire bans based on the selected date and return the bans and non-bans, with bans taking precedence.
   *
   * @param dateIndex - 0 for today, 1 for tomorrow
   * @returns - The filtered fire bans.
   */
  const getTotalFireBansByDateIndex = (dateIndex: number) => {
    const bansMap = new Map<string, TotalFireBanEntity>();

    hazards.value.totalFireBans.forEach((fireBan) => {
      const storedBan = bansMap.get(fireBan.lga);

      if (storedBan && storedBan.banLevel !== 'No ban') return; // keep any 'bans'

      if (fireBan.banLevel === 'No ban' || !fireBan.dateOfBan) {
        if (!storedBan) bansMap.set(fireBan.lga, fireBan); // store any new 'no bans'
        return;
      }

      const baseCurrentDate = DateTime.now().plus({ days: dateIndex });
      const currentDate = dateIndex === 0 ? baseCurrentDate : baseCurrentDate.startOf('day');
      const dateOfBan = fireBan.dateOfBan.startOf('day');
      const endOfBan = fireBan.expiry ?? dateOfBan.endOf('day');

      if (currentDate.diff(dateOfBan).milliseconds >= 0 && currentDate.diff(endOfBan).milliseconds < 0) {
        bansMap.set(fireBan.lga, fireBan); // store if the ban is in range
      }
    });

    return Array.from(bansMap.values());
  };

  /**
   * Handles the selected hazard.
   * @param selectedHazard - The selected hazard
   * @returns
   */
  const handleSelectedHazard = (hazard: SelectedHazard) => {
    if (selectedHazard.value?.hazardId === hazard.hazardId && selectedHazard.value?.viewType === hazard.viewType) {
      return;
    }

    let eventId = hazard?.eventId;

    if (!eventId && hazard?.hazardType) {
      eventId =
        ((hazards.value[hazard?.hazardType] as HazardEntity[])?.find((h: HazardEntity) => h.id === hazard?.hazardId) as HazardEntity)
          ?.eventId ?? '';
    }

    if (activeView.value !== HazardView.List && hazard.viewType === SelectedHazardViewType.Popup) {
      mapStore.selectedPopupHazard = { ...hazard, eventId };
    }
    if (hazard.viewType === SelectedHazardViewType.Modal) {
      mapStore.selectedPopupHazard = null;
    }

    setSelectedHazard({ ...hazard, eventId });
  };

  /**
   * Toggles the selected hazard by navigating to the hazard route.
   * If the hazard is not null, it will navigate to the hazard page.
   * If the hazard is null, it will navigate to the hazards page (and clear the selected hazard).
   *
   * @param hazard - The selected hazard or null
   */
  const toggleSelectHazard = (hazard: SelectedHazard | null) => {
    if (hazard) {
      router.push({
        name: RouteNames.HazardById,
        params: {
          hazardType: hazardTypeRoute(hazard.hazardType),
          hazardId: hazard.hazardId,
        },
        query: {
          display: hazard.viewType,
        },
      });
    } else {
      if (selectedHazard.value) {
        setSelectedHazard(null);
      }

      const currentParams = router.currentRoute.value?.params;
      const currentHazardType = currentParams?.hazardType;

      if (currentHazardType && [MapMode.FireBan, MapMode.FireDanger].includes(mapMode.value)) {
        router.push({
          name: RouteNames.HazardByType,
          params: {
            hazardType: currentParams?.hazardType,
          },
        });
      } else {
        router.push({
          name: RouteNames.Hazards,
        });
      }
    }
  };

  // Sources
  // KUDO1-6688, updates the hazard polygons source to include warnings and storm natural hazards polygons.
  // KUDO1-6830, updates the hazard polygons source to modify the earthquake polygons source to use circles with radius based on magnitude.
  const getHazardPolygonsGeoSource = computed((): FeatureCollection<Geometry, GeoJsonProperties> => {
    const sectionKeys: (keyof typeof filteredSectionHazards.value)[] = ['warnings', 'outagesAndClosures', 'centres', 'naturalHazards'];

    const features = sectionKeys
      .flatMap((key) =>
        filteredSectionHazards.value[key]
          .filter((hazard) => hazard.entitySubType !== EntitySubType.EarthquakeIncidentEntity)
          .flatMap((hazard) => hazard.polygonFeatures)
      )
      .filter(Boolean) as Feature<Geometry, GeoJsonProperties>[];

    return {
      type: 'FeatureCollection',
      features,
    };
  });

  const getHazardPolylinesGeoSource = computed((): FeatureCollection<Geometry, GeoJsonProperties> => {
    const sectionKeys: (keyof typeof filteredSectionHazards.value)[] = [
      'warnings',
      'incidents',
      'outagesAndClosures',
      'centres',
      'naturalHazards',
    ];

    const features = sectionKeys
      .flatMap((key) => filteredSectionHazards.value[key].flatMap((hazard) => hazard.polylineFeatures))
      .filter(Boolean) as Feature<Geometry, GeoJsonProperties>[];

    return {
      type: 'FeatureCollection',
      features,
    };
  });

  const getHazardFireShapePolygonsGeoSource = computed((): FeatureCollection<Geometry, GeoJsonProperties> => {
    const incidentsPolygonFeatures = filteredSectionHazards.value.incidents
      .flatMap((incident) => incident.polygonFeatures)
      .filter(Boolean) as Feature<Geometry, GeoJsonProperties>[];

    const mergedPolygonGeoSource: FeatureCollection<Geometry, GeoJsonProperties> = {
      type: 'FeatureCollection',
      features: incidentsPolygonFeatures,
    };

    return mergedPolygonGeoSource;
  });

  const getHazardPointGeoSource = computed((): FeatureCollection<Geometry, GeoJsonProperties> => {
    const sectionKeys: (keyof typeof filteredSectionHazards.value)[] = [
      'warnings',
      'incidents',
      'outagesAndClosures',
      'centres',
      'prescribedBurnsAndBurnOffs',
      'naturalHazards',
    ];

    const features = sectionKeys
      .flatMap((key) => filteredSectionHazards.value[key].flatMap((hazard) => hazard.pointFeatures))
      .filter(Boolean) as Feature<Geometry, GeoJsonProperties>[];

    const mergedPointGeoSource: FeatureCollection<Geometry, GeoJsonProperties> = {
      type: 'FeatureCollection',
      features,
    };

    return mergedPointGeoSource;
  });

  const getEarthquakeCircleFeaturesSource = computed((): FeatureCollection<Geometry, GeoJsonProperties> => {
    const earthquakeIncidents = filteredSectionHazards.value.naturalHazards.filter(
      (incident: IncidentEntity) => incident.entitySubType === EntitySubType.EarthquakeIncidentEntity && incident.earthquakeMagnitude
    );

    const earthquakePointFeatures = replaceEarthquakeCircleFeatures(earthquakeIncidents);

    const mergedPointGeoSource: FeatureCollection<Geometry, GeoJsonProperties> = {
      type: 'FeatureCollection',
      features: earthquakePointFeatures,
    };

    return mergedPointGeoSource;
  });

  const getPrescribedBurnsPointGeoSource = computed((): FeatureCollection<Geometry, GeoJsonProperties> => {
    const prescribedBurnsPointFeatures = hazards.value.prescribedBurns
      .flatMap((prescribedBurns) => prescribedBurns.pointFeatures)
      .filter(Boolean) as Feature<Geometry, GeoJsonProperties>[];

    const mergedPointGeoSource: FeatureCollection<Geometry, GeoJsonProperties> = {
      type: 'FeatureCollection',
      features: prescribedBurnsPointFeatures,
    };

    return mergedPointGeoSource;
  });

  return {
    hazards,
    mapMode,
    setMapMode,
    searchTerm,
    activeView,
    typesFilters,
    isSomeTypeFilterActive,
    retryButton,
    selectedHazard,
    isLoadingHazards,
    isLoadingEnrichedWarning,
    fetchHazards,
    setActiveView,
    getHazardsByEvent,
    fetchAllHazards,
    handleSelectedHazard,
    getHazardPointGeoSource,
    getEarthquakeCircleFeaturesSource,
    getHazardPolygonsGeoSource,
    getHazardFireShapePolygonsGeoSource,
    getHazardPolylinesGeoSource,
    filteredSectionHazards,
    selectedDateIndex,
    setSelectedDateIndex,
    dates,
    getPrescribedBurnsPointGeoSource,
    toggleSelectHazard,
    activeTotalFireBansForToday,
    extremeOrCatastrophicFireDangerRatingForToday,
    getTotalFireBansByDateIndex,
  };
});
